/**
 * Active Syncs Page
 * 
 * Real-time monitoring of active sync operations with WebSocket updates,
 * bulk actions, and comprehensive sync management
 */

import React, { useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  CircularProgress
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Stop as StopIcon
} from '@mui/icons-material'
import { syncApi, handleApiError } from '../../../services/api'
import ConfirmDialog from '../../../components/shared/dialogs/ConfirmDialog'
import SyncOperationsTable from '../../../components/SyncOperationsTable'
import LockManagement from '../../../components/LockManagement'
import ActiveSyncStatus from '../../../components/sync/ActiveSyncStatus'
import useActiveSyncStatus from '../../../hooks/useActiveSyncStatus'
import { poolwerxColors } from '../../../theme/colors'
import { Alert } from '@mui/material'
import AdminPageHeader from '../../../components/admin/shared/AdminPageHeader'
import { useNotify } from '../../../components/shared/ToastProvider'

export const ActiveSyncs: React.FC = () => {
  const { notify } = useNotify()
  const [selectedOperations, setSelectedOperations] = useState<string[]>([])

  // Bulk cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [cancellingOperations, setCancellingOperations] = useState<string[]>([])

  // Use unified hook for sync status
  const { 
    operations, 
    isLoading, 
    error,
    isConnected,
    connectionStatus,
    refetch,
    setError,
    stats
  } = useActiveSyncStatus({
    autoRefresh: true,
    // onSyncComplete callback removed - sync operations provide their own completion messages
    // to avoid confusion between completed vs cancelled syncs
    onSyncUpdate: (sync) => {
      if (sync.status === 'failed') {
        showSnackbar(`Sync failed: ${sync.fp_code} - ${sync.sync_system}`, 'error')
      }
    }
  })



  // Utility functions
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'info') => {
    notify({ message, severity })
  }

  // Handle manual refresh
  const handleRefresh = () => {
    refetch(true)
    showSnackbar('Active syncs refreshed', 'info')
  }

  // Handle individual operation cancel
  const handleCancelOperation = async (operationId: string) => {
    try {
      setCancellingOperations(prev => [...prev, operationId])
      await syncApi.cancelSync(operationId, 'Cancelled by admin')
      
      // Operations will be updated on next refetch
      refetch(true)
      showSnackbar('Sync operation cancelled successfully', 'success')
      
    } catch (error) {
      console.error('Failed to cancel sync operation:', error)
      showSnackbar(handleApiError(error), 'error')
    } finally {
      setCancellingOperations(prev => prev.filter(id => id !== operationId))
    }
  }

  // Handle refresh individual operation
  const handleRefreshOperation = async (operationId: string) => {
    try {
      const response = await syncApi.getProgress(operationId)
      const updatedOperation = response.data

      // Operations will be updated on next refetch
      refetch(true)
      
      showSnackbar('Operation refreshed', 'info')
      
    } catch (error) {
      console.error('Failed to refresh operation:', error)
      showSnackbar(handleApiError(error), 'error')
    }
  }

  // Handle bulk cancel
  const handleBulkCancel = () => {
    if (selectedOperations.length === 0) {
      showSnackbar('No operations selected', 'error')
      return
    }
    setCancelDialogOpen(true)
  }

  const confirmBulkCancel = async () => {
    try {
      setCancelDialogOpen(false)
      setCancellingOperations([...selectedOperations])

      const cancelPromises = selectedOperations.map(operationId => 
        syncApi.cancelSync(operationId, 'Bulk cancelled by admin')
      )

      await Promise.all(cancelPromises)

      // Operations will be updated on next refetch
      refetch(true)
      
      setSelectedOperations([])
      showSnackbar(`${selectedOperations.length} sync operations cancelled successfully`, 'success')
      
    } catch (error) {
      console.error('Failed to cancel sync operations:', error)
      showSnackbar(handleApiError(error), 'error')
    } finally {
      setCancellingOperations([])
    }
  }

  return (
    <>
      <Box>
        <AdminPageHeader
          pageKey="dataSync"
          titleOverride="Active Syncs"
          descriptionOverride="Real-time monitoring of sync operations and sync lock management"
          breadcrumbs={[
            { label: 'Admin', to: '/admin' },
            { label: 'Data Sync', to: '/admin/data-sync/active' },
            { label: 'Active Syncs' },
          ]}
          actions={
            selectedOperations.length > 0 ? (
              <Button
                variant="outlined"
                color="error"
                startIcon={<StopIcon />}
                onClick={handleBulkCancel}
                disabled={cancellingOperations.length > 0}
              >
                Cancel Selected ({selectedOperations.length})
              </Button>
            ) : undefined
          }
        />
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {/* Active Sync Status Display */}
        <ActiveSyncStatus
          operations={operations}
          isLoading={isLoading}
          showBulkActions={true}
          onRefresh={handleRefresh}
          showConnectionStatus={true}
          connectionStatus={{
            isConnected,
            status: connectionStatus
          }}
        />

        {/* Lock Management Section */}
        <LockManagement 
          onLockReleased={() => {
            // Refresh active syncs when locks are released
            refetch(false)
            showSnackbar('Locks refreshed - checking for unblocked syncs', 'info')
          }}
          refreshInterval={operations.length > 0 ? 15000 : 60000} // 15s when active, 60s when idle
        />

        {/* Results Summary */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            {`${operations.length} active sync operation${operations.length !== 1 ? 's' : ''}`}
          </Typography>

          {operations.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                {stats.running} Running • {stats.pending} Pending
              </Typography>
            </Box>
          )}
        </Box>

        {/* Operations Table */}
        <SyncOperationsTable
          operations={operations}
          isLoading={isLoading}
          showBulkActions={true}
          showProgress={true}
          onCancelOperation={handleCancelOperation}
          onRefreshOperation={handleRefreshOperation}
          onSelectionChange={setSelectedOperations}
          emptyMessage=""
        />

        {/* Loading Overlay for bulk operations */}
        {cancellingOperations.length > 0 && (
          <Box 
            sx={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 9999
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ color: poolwerxColors.teal, mb: 2 }} />
              <Typography variant="h6">
                Cancelling {cancellingOperations.length} operation{cancellingOperations.length !== 1 ? 's' : ''}...
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Bulk Cancel Confirmation Dialog */}
      <ConfirmDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={confirmBulkCancel}
        tone="warning"
        title="Cancel Multiple Sync Operations"
        confirmLabel="Confirm Cancel"
        cancelLabel="Keep Running"
        body={
          <Typography variant="body2">
            Are you sure you want to cancel {selectedOperations.length} selected sync
            operation{selectedOperations.length !== 1 ? 's' : ''}? This action cannot be undone.
          </Typography>
        }
      />
    </>
  )
}

export default ActiveSyncs